import { useEffect } from "react";
import {
    Box,
    Stack,
    Button,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormHelperText,
    Typography,
    Autocomplete,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import { useCompanySubscriptions } from "hooks/useCompanySubscriptions";
import { format, addDays } from "date-fns";
import { useSelector } from "react-redux";
import { GlobalAppDataStorageService } from "infra/storage";
import { blueGrey } from "@mui/material/colors";

export const SubscriptionForm = ({
    setIsDialogOpen,
    jobSlots: oldJobSlots = 0,
    talentRequests: oldTalentRequests = 0,
}) => {
    const companyId = useSelector(
        (state) => state.companySlice?.currentCompany?.id,
    );
    const { addSubscription, isLoading, requestSuccessful } =
        useCompanySubscriptions();

    const {
        handleSubmit,
        register,
        control,
        formState: { errors },
        setValue,
    } = useForm();

    // Get all plans
    const subscriptionPlans =
        GlobalAppDataStorageService.getSubscriptionPlans();
    // Derive actual packages from plans
    const subscriptionPackagesOptions = subscriptionPlans.flatMap(
        (plan) => plan.packages,
    );

    const subscriptionTagOptions =
        GlobalAppDataStorageService.getSubscriptionTags();

    useEffect(() => {
        if (requestSuccessful) {
            setIsDialogOpen(false);
        }
    }, [requestSuccessful, setIsDialogOpen]);

    const handleUpdateBasedOnPackage = (event) => {
        const value = event.target.value;

        const selectedPackage = subscriptionPlans
            .find((plan) => plan.packages.some((pkg) => pkg.id === value))
            ?.packages.find((pkg) => pkg.id === value);

        if (selectedPackage?.credits) {
            setValue("attCredit", selectedPackage.credits.att || 0);
            setValue("jobPostCredit", selectedPackage.credits.job_post || 0);
        }
        if (selectedPackage?.days) {
            const start = new Date();
            const end = addDays(start, selectedPackage.days);

            setValue("startDate", format(start, "yyyy-MM-dd"));
            setValue("endDate", format(end, "yyyy-MM-dd"));
        }
    };

    const submitForm = async (data) => {
        await addSubscription({
            ...data,
            companyId,
        });
    };

    return (
        <Box component="form" onSubmit={handleSubmit(submitForm)}>
            <Box gap={6} pt={3}>
                <input
                    {...register("subscriptionId")}
                    name="subscriptionId"
                    type="hidden"
                />

                <Stack gap={4}>
                    {/* Package */}
                    <Stack direction="row" gap={2}>
                        <FormControl
                            variant="standard"
                            error={errors.packageId}
                            fullWidth={true}
                        >
                            <InputLabel id="select-plan-label">
                                Select package
                            </InputLabel>
                            <Controller
                                name="packageId"
                                control={control}
                                rules={{
                                    required:
                                        "We cannot have a subscription without a package!",
                                }}
                                render={({ field }) => (
                                    <Select
                                        labelId="select-plan-label"
                                        {...field}
                                        onChange={(event) => {
                                            field.onChange(event);
                                            handleUpdateBasedOnPackage(event);
                                        }}
                                    >
                                        {subscriptionPackagesOptions.map(
                                            (option) => (
                                                <MenuItem
                                                    key={option.id}
                                                    value={option.id}
                                                >
                                                    {option.name}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Select>
                                )}
                            />
                            {errors.packageId && (
                                <FormHelperText>
                                    {errors.packageId.message}
                                </FormHelperText>
                            )}
                        </FormControl>
                    </Stack>

                    {/* Duration */}
                    <Stack direction="row" gap={2}>
                        <TextField
                            {...register("startDate")}
                            label="Start Date"
                            type="date"
                            variant="standard"
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />

                        <TextField
                            {...register("endDate")}
                            label="End Date"
                            type="date"
                            variant="standard"
                            fullWidth
                            InputLabelProps={{
                                shrink: true,
                            }}
                        />
                    </Stack>

                    {
                        // Talent requests & Job slots
                        <Stack direction="row" gap={2}>
                            <Stack gap={2} sx={{ width: "100%" }}>
                                <Stack
                                    p={1}
                                    backgroundColor={blueGrey[50]}
                                    borderRadius={2}
                                >
                                    <Typography variant="body2">
                                        This company already has
                                    </Typography>
                                    <Typography variant="body2">
                                        <b>{oldTalentRequests}</b> talent
                                        requests
                                    </Typography>
                                </Stack>

                                <TextField
                                    type="number"
                                    placeholder="Add/Remove credits"
                                    {...register("attCredit")}
                                    label="Talent requests"
                                    inputProps={{
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                        "aria-label":
                                            "Available talent requests",
                                    }}
                                    InputLabelProps={{ shrink: true }}
                                    error={!!errors.attCredit}
                                    helperText={
                                        errors.attCredit
                                            ? errors.attCredit.message
                                            : "To substract type a negative number eg -5"
                                    }
                                    size="small"
                                    variant="standard"
                                />
                            </Stack>

                            <Stack gap={2} sx={{ width: "100%" }}>
                                <Stack
                                    p={1}
                                    backgroundColor={blueGrey[50]}
                                    borderRadius={2}
                                >
                                    <Typography variant="body2">
                                        This company already has
                                    </Typography>
                                    <Typography variant="body2">
                                        <b>{oldJobSlots}</b> job slots
                                    </Typography>
                                </Stack>

                                <TextField
                                    type="number"
                                    label="Job slots"
                                    {...register("jobPostCredit")}
                                    placeholder="Add/remove credits"
                                    InputLabelProps={{ shrink: true }}
                                    inputProps={{
                                        inputMode: "numeric",
                                        pattern: "[0-9]*",
                                        "aria-label": "Job board credits input",
                                    }}
                                    size="small"
                                    variant="standard"
                                    helperText={
                                        errors.jobPostCredit
                                            ? errors.jobPostCredit.message
                                            : "To substract type a negative number eg -4"
                                    }
                                />
                            </Stack>
                        </Stack>
                    }

                    <Stack
                        px={1}
                        py={2}
                        sx={{
                            border: 1,
                            position: "relative",
                            borderColor: blueGrey[200],
                        }}
                        borderRadius={2}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                position: "absolute",
                                top: 0,
                                transform: "translateY(-50%)",
                                background: "background.paper",
                                px: "5px",
                                left: "5px",
                            }}
                        >
                            REPORTING
                        </Typography>
                        <Controller
                            name="tagIds"
                            control={control}
                            defaultValue={[]}
                            render={({ field }) => (
                                <Autocomplete
                                    multiple
                                    disableCloseOnSelect
                                    options={subscriptionTagOptions}
                                    getOptionLabel={(option) =>
                                        option?.name ?? ""
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                        option.id === value.id
                                    }
                                    value={subscriptionTagOptions.filter(
                                        (option) =>
                                            field.value.includes(option.id),
                                    )}
                                    onChange={(_, options) =>
                                        field.onChange(
                                            options.map((option) => option.id),
                                        )
                                    }
                                    onBlur={field.onBlur}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            inputRef={field.ref}
                                            variant="standard"
                                            label="Select Subscription Tags"
                                            helperText="Use a tag to note why this manual subscription is an exception. Tags are used in reporting."
                                        />
                                    )}
                                />
                            )}
                        />
                    </Stack>
                </Stack>
            </Box>

            <Stack mt={6} direction="row" spacing={2} justifyContent="flex-end">
                <Button
                    color="gray"
                    variant="text"
                    onClick={() => setIsDialogOpen(false)}
                >
                    Cancel
                </Button>
                <LoadingButton
                    color="primary"
                    type="submit"
                    loading={isLoading}
                    loadingPosition="start"
                    startIcon={<SaveIcon />}
                >
                    Save changes
                </LoadingButton>
            </Stack>
        </Box>
    );
};

export default SubscriptionForm;
